import _ from 'lodash';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import { RadioButton } from 'primereact/radiobutton';
import { useMemo } from 'react';
import { MinimizedLvCatalog } from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { formatDate } from '../../../Helper/Util/DateHelper';
import { EMPTY_FIELD_PLACEHOLDER } from '../../../Helper/Statics/Constants';

type AlphaLvCatalogPickerStepProps = {
  catalogs: Record<string, MinimizedLvCatalog[]>;
  selected: MinimizedLvCatalog | null;
  epCatalogVersionId?: string;
  readOnly?: boolean;
  onSelectionChange: (row: MinimizedLvCatalog) => void;
};

export default function AlphaLvCatalogPickerStep({
  catalogs,
  selected,
  epCatalogVersionId,
  readOnly = false,
  onSelectionChange
}: AlphaLvCatalogPickerStepProps) {
  const rows = useMemo(() => {
    const list: MinimizedLvCatalog[] = Object.values(catalogs ?? {}).flat();

    return _.sortBy(list, [
      // Desc createdAt via negative timestamp.
      (r) => -new Date(r.createdAt || 0).getTime(),
      (r) => String(r.filename || '').toLowerCase()
    ]);
  }, [catalogs]);

  return (
    <div>
      <div className={'mb-3'}>
        {'Wählen Sie ein Leistungsverzeichnis (LV) aus der LV-Katalogliste.'}
      </div>

      <DataTable
        value={rows}
        dataKey={'id'}
        emptyMessage={'Keine LV-Kataloge verfügbar'}
      >
        {!readOnly && (
          <Column
            header={''}
            style={{ width: '3.5rem', textAlign: 'center' }}
            headerStyle={{ width: '3.5rem' }}
            body={(row: MinimizedLvCatalog) => (
              <div className={'flex justify-content-center align-items-center'}>
                <RadioButton
                  inputId={'alpha-lv-cat-radio-' + row.id}
                  name={'alpha-lv-catalog-picker'}
                  value={row}
                  checked={!!selected && selected.id === row.id}
                  disabled={readOnly}
                  onChange={(e) => {
                    if (readOnly) return;
                    onSelectionChange(e.value as MinimizedLvCatalog);
                  }}
                />
              </div>
            )}
          />
        )}

        <Column
          header={'LV'}
          body={(row: MinimizedLvCatalog) =>
            row.title ? String(row.title) : EMPTY_FIELD_PLACEHOLDER
          }
        />
        <Column
          header={'EP-Katalog kompatibel'}
          body={(row: MinimizedLvCatalog) => {
            if (!epCatalogVersionId || !row.versionId) {
              return EMPTY_FIELD_PLACEHOLDER;
            }

            const mismatch = row.versionId !== epCatalogVersionId;
            if (!mismatch) {
              return (
                <span className={'valid-color'}>
                  <i className={'pi pi-check-circle'}></i>
                </span>
              );
            }

            const tooltipTargetId = 'alpha-lv-version-mismatch-' + row.id;
            return (
              <span id={tooltipTargetId} className={'warning-color'}>
                <i className={'pi pi-exclamation-triangle'}></i>
                <Tooltip
                  target={'#' + tooltipTargetId}
                  position="top"
                  mouseTrack
                  mouseTrackTop={10}
                >
                  Warnung: Es ist möglich, dass die EP-Positionen zwischen
                  EP-Katalog und LV abweichen, da die Versionen nicht
                  übereinstimmen.
                </Tooltip>
              </span>
            );
          }}
          style={{ width: '10rem', textAlign: 'center' }}
        />
        <Column
          header={'Erstellt am'}
          body={(row: MinimizedLvCatalog) => {
            if (!row.createdAt) return EMPTY_FIELD_PLACEHOLDER;
            return formatDate(row.createdAt);
          }}
          style={{ width: '10rem' }}
        />
        <Column
          header={'Zuletzt aktualisiert'}
          body={(row: MinimizedLvCatalog) => {
            if (!row.updatedAt) return EMPTY_FIELD_PLACEHOLDER;
            return formatDate(row.updatedAt);
          }}
          style={{ width: '11rem' }}
        />
      </DataTable>
    </div>
  );
}
